/**
 * UI controls wiring.
 *
 * Connects DOM elements to callbacks. Thin layer — all logic
 * lives in main.ts.
 */

export interface ControlElements {
  btnMic: HTMLButtonElement;
  btnNoise: HTMLButtonElement;
  btnMeasure: HTMLButtonElement;
  btnExport: HTMLButtonElement;
  btnDebug: HTMLButtonElement;
  noiseLevel: HTMLInputElement;
  noiseLevelDisplay: HTMLSpanElement;
  avgTime: HTMLSelectElement;
  smoothing: HTMLSelectElement;
  eqBands: HTMLInputElement;
  micStatus: HTMLSpanElement;
  sampleRate: HTMLSpanElement;
  measureProgress: HTMLDivElement;
  progressFill: HTMLDivElement;
  measureTime: HTMLSpanElement;
}

/**
 * Grab all control elements from the DOM.
 */
export function getControlElements(): ControlElements {
  return {
    btnMic: document.getElementById("btn-mic") as HTMLButtonElement,
    btnNoise: document.getElementById("btn-noise") as HTMLButtonElement,
    btnMeasure: document.getElementById("btn-measure") as HTMLButtonElement,
    btnExport: document.getElementById("btn-export") as HTMLButtonElement,
    btnDebug: document.getElementById("btn-debug") as HTMLButtonElement,
    noiseLevel: document.getElementById("noise-level") as HTMLInputElement,
    noiseLevelDisplay: document.getElementById("noise-level-display") as HTMLSpanElement,
    avgTime: document.getElementById("avg-time") as HTMLSelectElement,
    smoothing: document.getElementById("smoothing") as HTMLSelectElement,
    eqBands: document.getElementById("eq-bands") as HTMLInputElement,
    micStatus: document.getElementById("mic-status") as HTMLSpanElement,
    sampleRate: document.getElementById("sample-rate") as HTMLSpanElement,
    measureProgress: document.getElementById("measure-progress") as HTMLDivElement,
    progressFill: document.querySelector(".progress-fill") as HTMLDivElement,
    measureTime: document.getElementById("measure-time") as HTMLSpanElement,
  };
}

/**
 * Update the mic status badge.
 */
export function setMicStatus(
  el: ControlElements,
  active: boolean,
  sampleRate?: number
): void {
  el.micStatus.textContent = active ? "Mic On" : "Mic Off";
  el.micStatus.classList.toggle("on", active);
  el.micStatus.classList.toggle("off", !active);

  if (sampleRate) {
    el.sampleRate.textContent = `${sampleRate} Hz`;
  } else {
    el.sampleRate.textContent = "";
  }

  // Enable/disable mic-dependent controls (noise is independent)
  el.btnMeasure.disabled = !active;

  el.btnMic.textContent = active ? "Stop Microphone" : "Start Microphone";
}

/**
 * Update the noise button state.
 */
export function setNoiseState(el: ControlElements, playing: boolean): void {
  el.btnNoise.textContent = playing ? "Stop Pink Noise" : "Play Pink Noise";
  el.btnNoise.classList.toggle("active", playing);
}

/**
 * Update measurement progress.
 */
export function setMeasureProgress(
  el: ControlElements,
  state: "idle" | "capturing" | "complete",
  elapsed?: number,
  total?: number
): void {
  if (state === "idle") {
    el.measureProgress.classList.add("hidden");
    el.btnMeasure.textContent = "Start Measurement";
    el.btnMeasure.disabled = false;
  } else if (state === "capturing") {
    el.measureProgress.classList.remove("hidden");
    el.btnMeasure.textContent = "Stop Measurement";
    if (elapsed !== undefined && total !== undefined) {
      el.measureTime.textContent = `${elapsed.toFixed(1)} / ${total} s`;
      el.progressFill.style.width = `${(elapsed / total) * 100}%`;
    }
  } else if (state === "complete") {
    el.measureProgress.classList.add("hidden");
    el.btnMeasure.textContent = "Start Measurement";
    el.btnExport.disabled = false;
    el.btnDebug.disabled = false;
  }
}

/**
 * Show an error message to the user.
 */
export function showError(message: string): void {
  // Create a temporary error toast
  const toast = document.createElement("div");
  toast.className = "error-toast";
  toast.textContent = message;
  document.body.appendChild(toast);

  // Animate in
  requestAnimationFrame(() => toast.classList.add("visible"));

  // Remove after 5s
  setTimeout(() => {
    toast.classList.remove("visible");
    setTimeout(() => toast.remove(), 300);
  }, 5000);
}
